import tourFile from '@/config/tours.json'
import {FeatureToggle, isFeatureEnabled} from '@/helpers/featureToggle'
import {UserRoleContext} from '@/helpers/UserRole/UserRoleProvider'
import {UserRoleEnumSchema} from '@/types/UserRole'
import {GuidexLauncher, GuidexProvider, describeAnchor, useGuidex} from '@guidex/react'
import {PropsWithChildren, Suspense, lazy, useContext, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useLocation, useNavigate} from 'react-router-dom'
import {GuidexActivityContext} from './guidexActivity'
import {getGuidexEnvironment} from './guidexEnvironment'

const GuidexEditor = lazy(() => import('@guidex/react/editor').then(module => ({default: module.GuidexEditor})))

// Tours for pages behind a feature toggle are hidden while the page is disabled
const TOUR_FEATURE_TOGGLES: Record<string, (typeof FeatureToggle)[keyof typeof FeatureToggle]> = {
  'events-overview': FeatureToggle.realTimeNotificationPage,
  'message-templates-overview': FeatureToggle.messageTemplates,
}

const TOUR_EDITOR_ROLES: string[] = [
  UserRoleEnumSchema.enum.admin,
  UserRoleEnumSchema.enum['app-admin'],
  UserRoleEnumSchema.enum['super-admin'],
]

// The launcher sits over the empty bottom of the sidebar; the bottom-right corner holds the drawers' primary
// actions (Save, Continue). The editor toggle stacks above the launcher.
const EDITOR_TOGGLE_STYLE = {bottom: 'calc(var(--guidex-launcher-offset, 16px) + 48px)'}

const GuidexActivityProvider = ({children}: PropsWithChildren) => {
  const {isModalOpen, activeTour} = useGuidex()

  return (
    <GuidexActivityContext.Provider value={isModalOpen || activeTour !== null}>
      {children}
    </GuidexActivityContext.Provider>
  )
}

export const Guidex = ({children}: PropsWithChildren) => {
  const location = useLocation()
  const navigate = useNavigate()
  const {locale} = useIntl()
  const {userRole} = useContext(UserRoleContext)

  const canEditTours =
    isFeatureEnabled(FeatureToggle.guidexEditor) && !!userRole && TOUR_EDITOR_ROLES.includes(userRole)

  const tours = useMemo(
    () => ({
      ...tourFile,
      tours: tourFile.tours.filter(tour => {
        const featureToggle = TOUR_FEATURE_TOGGLES[tour.id]
        return !featureToggle || isFeatureEnabled(featureToggle)
      }),
    }),
    []
  )

  return (
    <GuidexProvider
      tours={tours}
      environment={getGuidexEnvironment()}
      locale={locale}
      pathname={location.pathname}
      navigate={pathname => navigate(pathname)}
      onAnchorMissing={event =>
        console.warn(`[Guidex] ${event.tourId}/${event.stepId}: anchor not found`, describeAnchor(event.anchor))
      }
    >
      <GuidexActivityProvider>{children}</GuidexActivityProvider>
      <GuidexLauncher className="guidex-launcher" position="bottom-left" />
      {canEditTours && (
        <Suspense fallback={null}>
          <GuidexEditor style={EDITOR_TOGGLE_STYLE} />
        </Suspense>
      )}
    </GuidexProvider>
  )
}
